/* screens/sales/PaymentLinksScreen.jsx — «Ссылки на оплату» в консоли (/console/payment-links).
   Рабочий инструмент менеджера: нашёл клиента по email → выбрал заведение → выбрал
   тариф → получил именную ссылку → скопировал и отправил в мессенджер. Всё на одном
   экране без перезагрузок. Ниже — журнал выданных ссылок: статусы, отзыв, повторное
   копирование. Доступ — гейт RequirePaymentLinksAccess (subscriptions.issue_payment_links). */

import React from 'react';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { DataTable } from '../../components/patterns/DataTable.jsx';
import { EmptyState, ErrorState } from '../../components/patterns/States.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Checkbox, Field, SearchInput, Segmented, Textarea, NumberInput, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import { fmt } from '../../lib/format.js';
import {
  createPaymentLink, listSellablePlans, listPaymentLinks, revokePaymentLink, searchConsoleClients,
} from '../../lib/api/salesConsole.js';

const { useState, useMemo, useCallback, useEffect, useRef } = React;
const { useFakeQuery } = hooks;
const { useNavigate } = RR;

const MIN_QUERY = 3;   // сервер не ищет по 1–2 символам (code:'query_too_short')

// Статус ссылки: цвет + глиф + текст. Одним цветом их различить нельзя —
// «Оплачена» и «Отозвана» для менеджера противоположны по смыслу.
const STATUS = {
  active:  { label: 'Активна',  tone: 'info',    icon: 'clock' },
  used:    { label: 'Оплачена', tone: 'ok',      icon: 'check-circle' },
  revoked: { label: 'Отозвана', tone: 'neutral', icon: 'x-circle' },
  expired: { label: 'Истекла',  tone: 'warn',    icon: 'alert-triangle' },
};
const KIND_LABEL = { business: 'бизнес', personal: 'личное' };

// Суммы приходят строками DRF ("3950.00") — в число приводим ровно здесь.
const rub = (v) => (v == null || v === '' ? '—' : fmt.money(Number(v)));
const isPaid = (v) => Number(v) > 0;

/** Тарифы, которые вообще можно выставить ссылкой.
 *
 * Фильтр ТОЛЬКО по цене — и это важно. `/console/plans/sellable/` уже отдаёт
 * исключительно `is_active=True`, а поля `is_active` в его ответе НЕТ. Пока
 * здесь стояла дополнительная проверка `p.is_active`, она вычислялась в
 * `undefined`, и форма выпуска ссылки не предлагала НИ ОДНОГО тарифа — при
 * любом заведённом. Единственный источник правды о продаваемости — сервер;
 * дублировать его условие полем, которого он не присылает, нельзя.
 */
export function selectSellable(plans) {
  return (plans || []).filter((p) => isPaid(p.price_rub));
}

/** Аудитория тарифа обязана совпадать с типом пространства (иначе 400 audience_mismatch). */
export function fitsWorkspace(plan, ws) {
  return !ws || !plan.audience || plan.audience === ws.kind;
}

/** Отбор тарифов для режима «создать новое заведение».
 *
 * Создаём мы БИЗНЕС-пространство, поэтому личный тариф здесь запрещён и
 * сервером (`audience_mismatch`), и тут: включившись в заведении, он не
 * показался бы клиенту во вкладке настроек — витрина делится по `audience`.
 * Тариф без `audience` (старый ответ из кэша) не прячем: решение об аудитории
 * принадлежит серверу, и молча вырезать такой тариф значило бы повторить
 * ошибку с дублированием серверного фильтра на фронте.
 */
export function fitsNewWorkspace(plan) {
  return !plan.audience || plan.audience === 'business';
}
const days = (n) => `${n} ${fmt.plural(Number(n), 'день', 'дня', 'дней')}`;

function dt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// apiFetch не бросает на HTTP-кодах, а useFakeQuery считает провалом только
// исключение — переводим одно в другое, иначе экран показывал бы пустоту вместо ошибки.
function unwrap(promise) {
  return promise.then((r) => {
    if (r.ok) return r.data;
    const err = new Error((r.data && r.data.detail) || 'Не удалось загрузить данные.');
    err.status = r.status;
    throw err;
  });
}

// Буфер обмена: navigator.clipboard есть не везде (не-https, старые webview) —
// без фолбэка менеджер отправил бы клиенту пустое сообщение.
async function writeClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* нет разрешения — пробуем фолбэк */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) { return false; }
}

// Копирование с подтверждением: без явного «Скопировано» непонятно, сработало ли.
function useCopy() {
  const toast = useToast();
  const [copiedKey, setCopiedKey] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const copy = useCallback(async (text, key) => {
    const ok = await writeClipboard(text);
    if (!ok) {
      toast.show('Скопировать не удалось — выделите ссылку и скопируйте вручную.', 'danger', 6000);
      return;
    }
    setCopiedKey(key);
    toast.show('Ссылка скопирована', 'success');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopiedKey(null), 2200);
  }, [toast]);
  return { copiedKey, copy };
}

/* ── Шаг 1: поиск клиента ── */
function ClientPicker({ value, onPick, onReset }) {
  const [query, setQuery] = useState('');
  const EMPTY = { loading: false, results: null, tooShort: false, error: null, exactId: null };
  const [state, setState] = useState(EMPTY);
  const seq = useRef(0);

  const run = useCallback(async (raw) => {
    const term = (raw || '').trim();
    if (!term) { setState(EMPTY); return; }
    if (term.length < MIN_QUERY) { setState({ ...EMPTY, tooShort: true }); return; }
    const my = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    const r = await searchConsoleClients(term);
    if (my !== seq.current) return;   // ответ обогнала более свежая выдача
    if (!r.ok) {
      setState({ ...EMPTY, error: (r.data && r.data.detail) || 'Не удалось выполнить поиск.' });
      return;
    }
    const code = r.data && r.data.code;
    setState({
      loading: false,
      results: (r.data && r.data.results) || [],
      tooShort: code === 'query_too_short',
      error: null,
      // Поиск подстрочный, и у клиента бывают «однофамильцы» по адресу
      // (business-demo@…, support-demo@…). Сервер выводит точное совпадение
      // первым, но менеджер обязан ВИДЕТЬ, что это оно: он выставляет счёт.
      exactId: (r.data && r.data.exact_match_id) || null,
    });
  }, []);

  if (value) {
    return (
      <div className="pl-picked">
        <span className="pl-picked__glyph" aria-hidden="true"><Icon name="user" size={16} /></span>
        <div className="pl-picked__id">
          <div className="pl-picked__email">{value.email}</div>
          <div className="t-caption text-dim">
            {value.full_name || 'Без имени'} · с {fmt.date(value.date_joined)}
          </div>
        </div>
        <Button variant="ghost" size="sm" iconLeft="refresh" onClick={() => { setQuery(''); setState(EMPTY); onReset(); }}>
          Сменить
        </Button>
      </div>
    );
  }

  const short = state.tooShort || (query.trim().length > 0 && query.trim().length < MIN_QUERY);
  return (
    <div className="pl-search">
      <SearchInput value={query} onChange={setQuery} onDebouncedChange={run} delay={350}
        placeholder="Email, имя или фамилия клиента…" aria-label="Поиск клиента" />
      {state.loading && <div className="pl-hint"><Spinner size="sm" /> Ищем…</div>}
      {!state.loading && short && (
        <div className="pl-hint">Введите минимум {MIN_QUERY} символа — по одной-двум буквам поиск не ищет.</div>
      )}
      {!state.loading && state.error && (
        <div className="pl-error"><Icon name="alert-circle" size={14} />{state.error}</div>
      )}
      {!state.loading && !short && state.results && state.results.length === 0 && (
        <div className="pl-hint">Никого не нашли. Проверьте email — клиент должен быть зарегистрирован.</div>
      )}
      {!state.loading && state.results && state.results.length > 0 && (
        <div className="pl-hits" role="listbox" aria-label="Найденные клиенты">
          {state.results.map((c) => (
            <button type="button" key={c.id} role="option" aria-selected="false"
              className={'pl-hit' + (c.id === state.exactId ? ' pl-hit--exact' : '')}
              onClick={() => onPick(c)}>
              <span className="pl-hit__email">{c.email}</span>
              {c.id === state.exactId && (
                <Pill tone="accent" size="sm">Точное совпадение</Pill>
              )}
              <span className="pl-hit__name t-caption text-dim">{c.full_name || 'Без имени'}</span>
              <span className="pl-hit__date t-caption text-mute">с {fmt.date(c.date_joined)}</span>
            </button>
          ))}
          {state.results.length > 1 && (
            <div className="pl-hint">
              Найдено несколько похожих адресов — сверьте email до выставления счёта.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Шаг 2: выбор пространства ── */
function WorkspacePicker({ workspaces, value, onChange }) {
  if (!workspaces || workspaces.length === 0) {
    return (
      <div className="pl-warn">
        <Icon name="alert-triangle" size={14} />
        <span>Клиент нигде не администратор пространства — выставить ссылку не на что.
          Ссылка меняет тариф пространства, а распоряжаться им может только его администратор.</span>
      </div>
    );
  }
  return (
    <div className="pl-ws-list" role="radiogroup" aria-label="Пространство клиента">
      {workspaces.map((w) => {
        const on = !!(value && value.id === w.id);   // role=radio требует булев aria-checked
        return (
          <button type="button" key={w.id} role="radio" aria-checked={on}
            className={'pl-ws' + (on ? ' is-on' : '')} onClick={() => onChange(w)}>
            <span className="pl-ws__head">
              <span className="pl-ws__name">{w.name}</span>
              <Pill tone={w.kind === 'business' ? 'info' : 'neutral'} size="sm" glyph={false}>
                {KIND_LABEL[w.kind] || w.kind}
              </Pill>
            </span>
            <span className="pl-ws__plan t-caption text-dim">
              Сейчас: {w.current_plan_name || w.current_plan || 'без тарифа'}
              {w.expires_at ? ` · до ${fmt.date(w.expires_at)}` : ''}
            </span>
            {on && <Icon name="check" size={16} className="pl-ws__tick" />}
          </button>
        );
      })}
    </div>
  );
}

/* ── Готовая ссылка ── */
/* ── Доставка ссылки клиенту ──
   Менеджеру нужен ответ на один вопрос: клиент это получил или нет. Поэтому
   статусы переводим в человеческие фразы, а не показываем сырые слаги, и
   отличаем «канала нет» (норма — Telegram привязан не у всех) от «не ушло»
   (повод позвонить). Ключи зеркалят `subscriptions/link_delivery.py`. */
const DELIVERY = {
  sent: { label: 'отправлено', tone: 'ok', icon: 'check-circle' },
  queued: { label: 'в очереди на отправку', tone: 'info', icon: 'clock' },
  no_channel: { label: 'не привязан', tone: 'neutral', icon: 'minus-circle' },
  blocked: { label: 'бот заблокирован клиентом', tone: 'warn', icon: 'alert-triangle' },
  disabled_by_user: { label: 'клиент отключил такие письма', tone: 'warn', icon: 'alert-triangle' },
  failed: { label: 'не отправилось', tone: 'bad', icon: 'x-circle' },
};
const CHANNEL_LABEL = { email: 'Почта', telegram: 'Telegram' };

// Хоть куда-то дошло? Нужно, чтобы не писать «отправлено клиенту», когда на
// деле не ушло никуда, — менеджер поверит и не отправит ссылку сам.
function sentSomewhere(delivery) {
  return ['email', 'telegram'].some((ch) => {
    const s = (delivery || {})[ch];
    return s === 'sent' || s === 'queued';
  });
}

function DeliveryReport({ delivery }) {
  const channels = ['email', 'telegram'].filter((ch) => (delivery || {})[ch]);
  if (!channels.length) return null;
  const nothing = !sentSomewhere(delivery);
  return (
    <div className={'pl-delivery' + (nothing ? ' pl-delivery--bad' : '')}>
      {channels.map((ch) => {
        const d = DELIVERY[delivery[ch]] || { label: delivery[ch], tone: 'neutral' };
        return (
          <span key={ch} className="pl-delivery__row">
            <Pill tone={d.tone} icon={d.icon} size="sm">{CHANNEL_LABEL[ch]}: {d.label}</Pill>
          </span>
        );
      })}
      {nothing && (
        <span className="t-caption">Клиент ссылку не получил — отправьте её сами.</span>
      )}
    </div>
  );
}

function IssuedLink({ link, onMore }) {
  const { copiedKey, copy } = useCopy();
  const copied = copiedKey === 'issued';
  return (
    <div className="pl-result">
      <div className="pl-result__head">
        <Icon name="check-circle" size={18} />
        <span className="t-label">
          {sentSomewhere(link.delivery) ? 'Ссылка готова и отправлена клиенту' : 'Ссылка готова — отправьте её клиенту'}
        </span>
      </div>
      <div className="pl-url" title={link.url}>{link.url}</div>
      <div className="pl-result__meta t-caption text-dim">
        {/* В режиме «новое пространство» `link.workspace` — ЯКОРЬ, личное
            пространство клиента. Показать его именем значило бы отчитаться
            менеджеру о продаже личного тарифа вместо заведения. */}
        {link.client && link.client.email} · {link.mode === 'new_workspace'
          ? `новое пространство${link.new_workspace_name ? ` «${link.new_workspace_name}»` : ' (название введёт клиент)'}`
          : (link.workspace && link.workspace.name)} · {link.plan && link.plan.name} · {rub(link.amount_rub)}
        {link.expires_at ? ` · действует до ${dt(link.expires_at)}` : ''}
      </div>
      <DeliveryReport delivery={link.delivery} />
      <div className="cluster cluster-8">
        <Button variant="primary" iconLeft={copied ? 'check' : 'copy'} onClick={() => copy(link.url, 'issued')}>
          {copied ? 'Скопировано' : 'Скопировать ссылку'}
        </Button>
        <Button variant="ghost" iconLeft="plus" onClick={onMore}>Выставить ещё одну</Button>
      </div>
      <span className="pl-sr" role="status" aria-live="polite">{copied ? 'Ссылка скопирована в буфер обмена' : ''}</span>
    </div>
  );
}

/* ── Форма «Выставить ссылку» ── */
function IssueForm({ plans, ttlDefault, ttlMax, onIssued }) {
  const [client, setClient] = useState(null);
  const [ws, setWs] = useState(null);
  // Режим продажи: тариф существующему пространству или новое заведение,
  // которое создастся после оплаты. По умолчанию — прежнее поведение.
  const [mode, setMode] = useState('existing');
  // Заготовка названия. Менеджер обычно знает его из переговоров — тогда и
  // письмо со счётом называет ресторан, а не «новое пространство». Клиент
  // подтвердит или поправит на странице оплаты, поэтому поле необязательное.
  const [newWsName, setNewWsName] = useState('');
  const [planSlug, setPlanSlug] = useState('');
  const [ttl, setTtl] = useState(ttlDefault);
  // Автопродление — по умолчанию ДА: так продают почти всегда, и прежние ссылки
  // вели себя именно так. Разовая продажа — осознанное отклонение менеджера.
  const [autoRenew, setAutoRenew] = useState(true);
  const [note, setNote] = useState('');
  // Отправляем клиенту по умолчанию — за этим менеджер сюда и пришёл. Выключают
  // редко: когда перешлют ссылку сами в мессенджере, где идёт переписка.
  const [sendToClient, setSendToClient] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Выпущенная ссылка живёт ВНУТРИ формы: подменять форму отдельным экраном нельзя —
  // размонтирование потеряет выбранного клиента, а у сетевого их несколько подряд.
  const [issued, setIssued] = useState(null);

  // Дефолт/максимум срока приходят вместе со списком ссылок — он грузится параллельно.
  useEffect(() => { setTtl(ttlDefault); }, [ttlDefault]);

  const pickClient = (c) => {
    setClient(c);
    setError(null);
    setIssued(null);
    // Одно пространство — выбирать не из чего, экономим менеджеру клик.
    setWs((c.workspaces || []).length === 1 ? c.workspaces[0] : null);
  };
  const resetClient = () => {
    setClient(null); setWs(null); setError(null); setIssued(null);
    setMode('existing'); setNewWsName('');
  };
  const newWs = mode === 'new_workspace';
  const newWsNameClean = newWsName.trim();

  // Продать можно только активный и платный тариф: бесплатный бэк отобьёт (plan_is_free).
  const sellable = useMemo(() => selectSellable(plans), [plans]);

  // Аудитория тарифа обязана совпадать с типом пространства (иначе 400 audience_mismatch) —
  // не показываем то, что заведомо не выпустится.
  const options = useMemo(() => {
    const list = sellable.filter((p) => (newWs ? fitsNewWorkspace(p) : fitsWorkspace(p, ws)));
    const opt = (p) => ({
      value: p.slug,
      label: p.name,
      group: p.is_private ? 'Закрытые тарифы' : 'Публичные тарифы',
      icon: p.is_private ? 'lock' : undefined,
      hint: `${rub(p.price_rub)} · ${days(p.period_days)}`,
    });
    // Закрытые — первыми: ради них менеджер сюда и пришёл.
    return [...list.filter((p) => p.is_private).map(opt), ...list.filter((p) => !p.is_private).map(opt)];
  }, [sellable, ws, newWs]);

  const hiddenByAudience = (ws || newWs) ? sellable.length - options.length : 0;
  const plan = useMemo(() => sellable.find((p) => p.slug === planSlug) || null, [sellable, planSlug]);

  // Смена пространства могла увести выбранный тариф из списка — снимаем выбор,
  // иначе сводка показывала бы тариф, которого в поле уже нет.
  useEffect(() => {
    if (planSlug && !options.some((o) => o.value === planSlug)) setPlanSlug('');
  }, [options, planSlug]);

  // Разовый тариф (renewable=false, напр. «Быстрый старт») автопродления не
  // допускает в принципе — выбирать нечего, и сервер всё равно погасит флаг
  // (`PaymentLink.is_recurring`). Показываем факт, а не мёртвый переключатель.
  const canAutoRenew = !!(plan && plan.renewable);
  const willAutoRenew = autoRenew && canAutoRenew;

  const ttlNum = Number(ttl);
  const ttlValid = Number.isFinite(ttlNum) && ttlNum >= 1 && ttlNum <= ttlMax;
  // В режиме «новое заведение» пространства ещё нет — и это не пробел в форме:
  // получателя заменяет якорь (личное пространство клиента), который выбирает
  // сервер. Название необязательно: его подтвердит клиент перед оплатой.
  const targetReady = newWs ? true : !!ws;
  const ready = !!(client && targetReady && plan && ttlValid);

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    const r = await createPaymentLink({
      client_id: client.id, plan_slug: plan.slug,
      ttl_days: ttlNum, note: note.trim(), send_to_client: sendToClient,
      auto_renew: willAutoRenew,
      mode,
      // `workspace_id` в режиме нового заведения не шлём вовсе: якорь выбирает
      // сервер. Прислать сюда что-нибудь «на всякий случай» значило бы дать
      // форме влиять на то, к чему привяжется платёж.
      ...(newWs ? { new_workspace_name: newWsNameClean } : { workspace_id: ws.id }),
    });
    setBusy(false);
    if (!r.ok) {
      // detail с бэка — готовый русский текст, показываем как есть.
      setError((r.data && r.data.detail) || 'Не удалось выставить ссылку. Попробуйте ещё раз.');
      return;
    }
    setNote('');
    setPlanSlug('');
    setAutoRenew(true);    // следующая продажа — снова обычная, с автопродлением
    setWs(null);           // следующая ссылка сетевого клиента — обычно на другое заведение
    setNewWsName('');      // название — всегда про конкретную продажу
    setIssued(r.data);
    onIssued(r.data);
  };

  return (
    <div className="pl-form">
      {issued && <IssuedLink link={issued} onMore={() => setIssued(null)} />}

      <Field label="1. Клиент" hint={client ? undefined : 'Ищем по email, имени и фамилии — минимум ' + MIN_QUERY + ' символа.'}>
        <ClientPicker value={client} onPick={pickClient} onReset={resetClient} />
      </Field>

      {client && (
        <Field label="2. За какое пространство платим"
          hint={newWs
            ? 'Бизнес-пространство создастся после оплаты. Клиент подтвердит название на странице оплаты — там же он может его изменить.'
            : ((client.workspaces || []).length > 1 ? 'Тариф сменится именно у выбранного пространства.' : undefined)}>
          <Segmented value={mode} onChange={(v) => { setMode(v); setError(null); }}
            ariaLabel="Куда включаем тариф"
            options={[
              { value: 'existing', label: 'Готовое пространство', icon: 'building' },
              { value: 'new_workspace', label: 'Создать новое', icon: 'plus' },
            ]} />
          {!newWs && (
            <WorkspacePicker workspaces={client.workspaces} value={ws} onChange={(w) => { setWs(w); setError(null); }} />
          )}
        </Field>
      )}

      {client && newWs && (
        <Field label="Название заведения (необязательно)"
          hint={newWsNameClean
            ? 'Клиент увидит его в счёте и сможет поправить перед оплатой.'
            : 'Оставьте пустым — клиент введёт название сам. Вписанное здесь попадёт в письмо со счётом.'}>
          <TextInput value={newWsName} maxLength={120}
            placeholder="Например: Кафе «Пельмень» на Кирочной"
            onChange={(e) => { setNewWsName(e.target.value); setError(null); }} />
        </Field>
      )}

      {client && targetReady && (
        <>
          <Field label="3. Тариф"
            hint={hiddenByAudience > 0
              ? `Показаны тарифы для ${newWs ? 'бизнес' : (KIND_LABEL[ws.kind] || ws.kind)}-пространства. Скрыто по типу: ${hiddenByAudience}.`
              : 'Бесплатные тарифы ссылкой не выставляются.'}>
            {options.length === 0
              ? <div className="pl-warn"><Icon name="alert-triangle" size={14} />
                <span>Платных тарифов для этого типа пространства нет. Заведите закрытый тариф в разделе «Тарифы» → «Закрытые».</span></div>
              : <Select value={planSlug} onChange={setPlanSlug} options={options}
                placeholder="Выберите тариф…" searchable={options.length > 6} ariaLabel="Тариф" />}
          </Field>

          {plan && (
            <Field label="4. Автопродление"
              hint={canAutoRenew
                ? (willAutoRenew
                  ? 'После оплаты картой тариф продлевается сам, пока клиент не отключит. Иностранной картой такую ссылку оплатить нельзя — этот канал работает только с разовыми платежами.'
                  : 'Клиент платит один раз, карта не привязывается, следующий период — новой ссылкой. Зато к оплате добавляется иностранная карта — для тех, кто платит из-за рубежа.')
                : 'Тариф разовый по своей природе — автопродления по нему не бывает. Клиенту будет доступна и оплата иностранной картой.'}>
              {canAutoRenew
                ? <Segmented value={autoRenew ? 'on' : 'off'} onChange={(v) => setAutoRenew(v === 'on')}
                  ariaLabel="Автопродление"
                  options={[
                    { value: 'on', label: 'С автопродлением', icon: 'refresh' },
                    { value: 'off', label: 'Разовый платёж', icon: 'card' },
                  ]} />
                : <div className="pl-warn"><Icon name="alert-triangle" size={14} />
                  <span>Разовый платёж — автопродление этим тарифом не предусмотрено.</span></div>}
            </Field>
          )}

          <div className="pl-row2">
            <Field label="5. Срок действия ссылки"
              hint={`По умолчанию ${days(ttlDefault)}, максимум ${days(ttlMax)}.`}
              error={ttl !== '' && !ttlValid ? `Допустимо от 1 до ${ttlMax} дней.` : undefined}>
              <NumberInput value={ttl} onChange={setTtl} min={1} max={ttlMax} unit="дн." aria-label="Срок действия в днях" />
            </Field>
            <Field label="6. Комментарий" hint={`Виден только своим. ${note.length}/200`}>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} minRows={2} maxRows={4}
                placeholder="Например: договорились по телефону, сеть «Пельмень», оплата до пятницы." />
            </Field>
          </div>
        </>
      )}

      {ready && (
        <div className="pl-sum">
          <div className="pl-sum__title t-label">Проверьте перед выпуском</div>
          <p className="pl-sum__text">
            Ссылка для <b>{client.email}</b>{client.full_name ? ` (${client.full_name})` : ''}{' '}
            {newWs
              ? <>на <b>новое бизнес-пространство</b>
                {newWsNameClean ? <> «<b>{newWsNameClean}</b>»</> : ' (название введёт клиент)'}</>
              : <>на пространство <b>«{ws.name}»</b> ({KIND_LABEL[ws.kind] || ws.kind})</>}
            . Тариф <b>«{plan.name}»</b>
            {plan.is_private ? ' (закрытый)' : ''} — {rub(plan.price_rub)} за {days(plan.period_days)}.
            Ссылка действует {days(ttlNum)} с момента выпуска.
          </p>
          {/* Способ оплаты — следствие выбора автопродления, и менеджер обязан
              увидеть это ДО выпуска: «клиенту не предложили иностранную карту»
              выясняется иначе уже из его звонка. */}
          <p className="pl-sum__text">
            {willAutoRenew
              ? <><b>С автопродлением.</b> Оплата российской картой или по счёту для юрлица; иностранная карта по такой ссылке недоступна.</>
              : <><b>Разовый платёж.</b> Автосписаний не будет, следующий период — новой ссылкой. К оплате доступна и иностранная карта.</>}
          </p>
          <p className="pl-sum__now t-caption text-dim">
            {newWs
              ? <>После оплаты пространство создастся автоматически, сядет на «{plan.name}» и сразу
                  станет у клиента выбранным. Ни он, ни вы больше ничего не нажимаете.</>
              : <>Сейчас в пространстве: {ws.current_plan_name || ws.current_plan || 'без тарифа'}
                {ws.expires_at ? ` до ${fmt.date(ws.expires_at)}` : ''} → после оплаты станет «{plan.name}».</>}
          </p>
          <div className="pl-sum__amount">
            <span className="t-caption text-dim">Клиент заплатит</span>
            <span className="pl-sum__money num">{rub(plan.price_rub)}</span>
          </div>
        </div>
      )}

      {client && targetReady && (
        <div className="pl-send">
          {/* `onChange` у Checkbox отдаёт СОБЫТИЕ, а не булево (в отличие от
              Switch). Сюда был передан сам сеттер — и снятие галочки клало в
              состояние SyntheticEvent: галочка не снималась, а тело запроса
              становилось нестрингифицируемым, то есть кнопка «Выставить
              ссылку» переставала работать МОЛЧА, без ошибки на экране. */}
          <Checkbox checked={sendToClient} onChange={(e) => setSendToClient(e.target.checked)}
            label="Отправить клиенту сразу" />
          <span className="t-caption text-dim">
            {sendToClient
              ? <>Письмо на <b>{client.email}</b>{' '}
                и сообщение в Telegram, если аккаунт привязан.</>
              : 'Ссылку скопируете сами после выпуска.'}
          </span>
        </div>
      )}

      {error && <div className="pl-error"><Icon name="alert-circle" size={14} />{error}</div>}

      <div className="cluster cluster-8">
        <Button variant="primary" iconLeft="link" disabled={!ready} loading={busy} onClick={submit}>
          Выставить ссылку
        </Button>
        {!ready && (
          <span className="t-caption text-mute">
            {newWs ? 'Заполните клиента и тариф.' : 'Заполните клиента, пространство и тариф.'}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Журнал выданных ссылок ── */
function LinksTable({ links, onRevoke, revokingId }) {
  const { copiedKey, copy } = useCopy();

  const columns = useMemo(() => [
    {
      key: 'status', label: 'Статус',
      render: (r) => {
        const s = STATUS[r.status] || { label: r.status, tone: 'neutral' };
        return (
          <div className="pl-cell">
            <Pill tone={s.tone} icon={s.icon} size="sm">{s.label}</Pill>
            {/* Отзыв не догоняет уже подписанный счёт Робокассы: URL у клиента
                на руках. Раз оплата всё-таки прошла после отзыва/истечения,
                статус честно показывает «Оплачена» — но без этой пометки
                менеджер не узнал бы, что его отзыв не сработал. */}
            {r.paid_after_close && (
              <Pill tone="warn" icon="alert-triangle" size="sm">Оплачена после закрытия</Pill>
            )}
            {/* Счёт на существующее пространство вправе оплатить не клиент, а
                его бухгалтер или учредитель. Без пометки разговор «клиент
                говорит, что не платил» не сходится: платёж есть, а человек, с
                которым менеджер вёл переговоры, о нём может и не знать. */}
            {r.paid_by_guest && (
              <Pill tone="neutral" icon="user" size="sm">Оплатил не клиент</Pill>
            )}
            {/* «Клиент не платит» и «клиент не получил ссылку» — разные
                разговоры. Метку показываем, только когда доставка ПРОВАЛИЛАСЬ:
                успешная отправка — норма и в журнале лишний шум. */}
            {r.delivery && !sentSomewhere(r.delivery)
              && (r.delivery.email || r.delivery.telegram) && (
              <Pill tone="bad" icon="x-circle" size="sm">Не доставлена</Pill>
            )}
          </div>
        );
      },
    },
    {
      key: 'client', label: 'Клиент',
      render: (r) => (
        <div className="pl-cell">
          <span className="pl-cell__main">{(r.client && r.client.email) || '—'}</span>
          {r.client && r.client.full_name && <span className="t-caption text-dim">{r.client.full_name}</span>}
          {r.note && <span className="t-caption text-mute pl-cell__note" title={r.note}>{r.note}</span>}
        </div>
      ),
    },
    {
      key: 'workspace', label: 'Пространство',
      // В режиме «новое пространство» поле `workspace` ссылки — ЯКОРЬ, то есть
      // ЛИЧНОЕ пространство клиента. Показать его именем значило бы сообщить
      // менеджеру, что продан личный тариф, тогда как продано заведение.
      render: (r) => {
        if (r.mode === 'new_workspace') {
          const created = r.created_workspace && r.created_workspace.name;
          return (
            <div className="pl-cell">
              <span className="pl-cell__main">
                {created || r.new_workspace_name || 'Название введёт клиент'}
              </span>
              <span className="t-caption text-dim">
                {created ? 'новое пространство, создано' : 'новое пространство, будет создано после оплаты'}
              </span>
            </div>
          );
        }
        return (
          <div className="pl-cell">
            <span className="pl-cell__main">{(r.workspace && r.workspace.name) || '—'}</span>
            <span className="t-caption text-dim">{(r.workspace && KIND_LABEL[r.workspace.kind]) || ''}</span>
          </div>
        );
      },
    },
    {
      key: 'plan', label: 'Тариф',
      render: (r) => (
        <div className="pl-cell">
          <span className="pl-cell__main">{(r.plan && r.plan.name) || '—'}</span>
          {r.plan && r.plan.is_private && <Pill tone="neutral" icon="lock" size="sm">Закрытый</Pill>}
          {/* Пометку ставим только у разовых: автопродление — обычный случай, и
              пометка на каждой строке journal'а превратилась бы в шум. Зато
              «почему у клиента не списалось в следующем месяце» разбирается
              именно здесь. */}
          {!r.auto_renew && (
            <span className="t-caption text-mute">Разовый платёж, без автопродления</span>
          )}
        </div>
      ),
    },
    {
      key: 'amount', label: 'Сумма',
      render: (r) => (
        <div className="pl-cell">
          <span className="pl-cell__main num">{rub(r.amount_rub)}</span>
          {/* Расхождение молча всплыло бы только вопросом клиента: спишется сумма из ссылки. */}
          {r.price_drifted && (
            <span className="pl-drift" title="Цена тарифа изменилась после выпуска ссылки. Клиент заплатит сумму, зафиксированную в ссылке.">
              <Icon name="alert-triangle" size={12} />Цена тарифа изменилась
            </span>
          )}
        </div>
      ),
    },
    { key: 'issued_by', label: 'Выставил', render: (r) => (r.issued_by && r.issued_by.email) || '—' },
    { key: 'created_at', label: 'Создана', render: (r) => <span className="pl-nowrap num">{dt(r.created_at)}</span> },
    { key: 'expires_at', label: 'Действует до', render: (r) => <span className="pl-nowrap num">{dt(r.expires_at)}</span> },
    { key: 'used_at', label: 'Оплачена', render: (r) => <span className="pl-nowrap num">{dt(r.used_at)}</span> },
    {
      key: 'actions', label: 'Действия',
      render: (r) => (
        <div className="pl-row-actions">
          {r.url && (
            <Button variant="ghost" size="sm" iconLeft={copiedKey === r.id ? 'check' : 'copy'} onClick={() => copy(r.url, r.id)}>
              {copiedKey === r.id ? 'Скопировано' : 'Ссылка'}
            </Button>
          )}
          {/* Оплаченную отозвать нельзя (link_already_used) — кнопки у таких строк нет вовсе. */}
          {r.status === 'active' && (
            <Button variant="ghost" size="sm" iconLeft="x-circle"
              loading={revokingId === r.id} disabled={revokingId != null}
              onClick={() => onRevoke(r)}>
              {revokingId === r.id ? 'Отзываем…' : 'Отозвать'}
            </Button>
          )}
        </div>
      ),
    },
  ], [copiedKey, copy, onRevoke, revokingId]);

  return (
    <div className="pl-table">
      <DataTable columns={columns} rows={links} rowKey="id" compact
        cardTitle={(r) => (r.client && r.client.email) || 'Ссылка'} />
    </div>
  );
}

function PaymentLinksScreen() {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  // Счётчики РАЗДЕЛЬНЫЕ, и это не микрооптимизация. Пока он был общим, выпуск
  // ссылки перезагружал и список тарифов: `plansQ.loading` поднимался, ветка
  // рендера уходила на спиннер, `IssueForm` РАЗМОНТИРОВАЛСЯ — и уносил с собой
  // блок «Ссылка готова» вместе с самим адресом. Менеджер видел пустую форму
  // после успешного выпуска, считал, что не сработало, и выставлял второй счёт.
  // От выпуска ссылки список тарифов не меняется — перезагружать его незачем.
  const [nonce, setNonce] = useState(0);        // журнал выданных ссылок
  const [plansNonce, setPlansNonce] = useState(0);   // список тарифов (кнопка «Обновить»)

  // ВАЖНО: sellable-эндпоинт, а не /console/plans/. Тот закрыт правом
  // manage_private_plans, которого у менеджера по продажам нет — форма выпуска
  // падала бы в 403 у той самой роли, ради которой раздел и сделан.
  const plansQ = useFakeQuery(`pl-plans-${plansNonce}`, () => unwrap(listSellablePlans()), []);
  const linksQ = useFakeQuery(`pl-links-${status}-${query}-${nonce}`, () => unwrap(listPaymentLinks({ status, q: query })), []);

  const plans = (plansQ.data && plansQ.data.plans) || [];
  const links = (linksQ.data && linksQ.data.links) || [];
  const linksTotal = (linksQ.data && linksQ.data.total != null) ? linksQ.data.total : links.length;
  const truncated = (linksQ.data && linksQ.data.truncated) || 0;
  const ttlDefault = (linksQ.data && linksQ.data.ttl_default_days) || 30;
  const ttlMax = (linksQ.data && linksQ.data.ttl_max_days) || 90;
  // Журнал — после выпуска/отзыва. Тарифы НЕ трогаем: их перезагрузка
  // размонтирует форму вместе с результатом выпуска (см. комментарий у nonce).
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  // Полный рефреш — только по кнопке «Обновить»: там форма пустая и терять нечего.
  const reloadAll = useCallback(() => {
    setNonce((n) => n + 1);
    setPlansNonce((n) => n + 1);
  }, []);

  // id отзываемой ссылки: без него кнопка оставалась активной весь запрос и
  // перезагрузку журнала, а строка ещё секунду показывала статус «Активна» —
  // менеджер жмёт второй раз, не поняв, сработало ли.
  const [revoking, setRevoking] = useState(null);
  const revoke = useCallback(async (link) => {
    if (revoking) return;
    const ok = await confirm({
      title: 'Отозвать ссылку?',
      body: `Ссылка для ${(link.client && link.client.email) || 'клиента'} на тариф «${(link.plan && link.plan.name) || '—'}» перестанет открываться. Вернуть её нельзя — придётся выставить новую.`,
      confirmLabel: 'Отозвать', cancelLabel: 'Отмена', tone: 'danger',
    });
    if (!ok) return;
    setRevoking(link.id);
    // apiFetch не ловит отказ самого fetch (client.js — голый await fetch):
    // без catch кнопка навсегда осталась бы в состоянии «Отзываем…».
    const r = await revokePaymentLink(link.id)
      .catch(() => ({ ok: false, status: 0, data: null }));
    setRevoking(null);
    if (!r.ok) {
      toast.show((r.data && r.data.detail) || 'Не удалось отозвать ссылку.', 'danger', 6000);
      reload();   // состояние ссылки на бэке уже другое — подтягиваем актуальное
      return;
    }
    toast.show('Ссылка отозвана', 'success');
    reload();
  }, [confirm, toast, reload, revoking]);

  const filtered = !!(status || query);
  const actions = (
    <>
      <Button variant="ghost" iconLeft="arrow-left" onClick={() => navigate('/console')}>Сервисы</Button>
      <Button variant="secondary" iconLeft="refresh" onClick={reloadAll}>Обновить</Button>
    </>
  );

  return (
    <div className="section-page">
      <PageHeader breadcrumbs={[{ label: 'Админка', to: '/console' }, { label: 'Ссылки на оплату' }]}
        title="Ссылки на оплату" actions={actions} />

      <div className="section-body pl-body">
        <section className="pl-card">
          <div className="pl-card__head">
            <span className="t-h4">Выставить ссылку</span>
            <span className="t-caption text-dim">Именная ссылка на тариф — для клиента, с которым договорились</span>
          </div>
          <div className="pl-card__body">
            {plansQ.loading ? (
              <div className="pl-center"><Spinner size="lg" label="Загрузка тарифов…" /></div>
            ) : plansQ.error ? (
              <ErrorState title="Не удалось загрузить тарифы"
                body="Без списка тарифов ссылку не выставить." onRetry={plansQ.reload} />
            ) : (
              <IssueForm plans={plans} ttlDefault={ttlDefault} ttlMax={ttlMax}
                onIssued={() => { reload(); toast.show('Ссылка выставлена', 'success'); }} />
            )}
          </div>
        </section>

        <section className="pl-card">
          <div className="pl-card__head">
            <span className="t-h4">Выданные ссылки</span>
            {links.length > 0 && (
              <span className="t-caption text-dim">
                {linksTotal} {fmt.plural(linksTotal, 'ссылка', 'ссылки', 'ссылок')}
                {/* Молчаливое усечение читается как «это всё» — а здесь это
                    утверждение про деньги: менеджер решит, что больше не выставлял. */}
                {truncated > 0 && ` · показаны последние ${links.length}, ещё ${truncated} скрыто`}
              </span>
            )}
          </div>
          <div className="pl-card__body">
            <div className="pl-toolbar">
              {/* только onDebouncedChange: query — ключ запроса, на каждый символ в бэк не ходим */}
              <SearchInput value={query} onDebouncedChange={setQuery} delay={350}
                placeholder="Клиент, пространство или тариф…" className="pl-toolbar__search" aria-label="Поиск по ссылкам" />
              <div className="pl-toolbar__filter">
                <Select value={status} onChange={setStatus} searchable={false} ariaLabel="Статус ссылки"
                  placeholder="Все статусы"
                  options={[{ value: '', label: 'Все статусы' },
                    ...Object.keys(STATUS).map((k) => ({ value: k, label: STATUS[k].label, icon: STATUS[k].icon }))]} />
              </div>
            </div>

            {linksQ.loading && !linksQ.data ? (
              <div className="pl-center"><Spinner size="lg" label="Загрузка ссылок…" /></div>
            ) : linksQ.error ? (
              <ErrorState title="Не удалось загрузить ссылки" onRetry={reload} />
            ) : links.length === 0 ? (
              filtered
                ? <EmptyState icon="search" title="Ничего не нашли"
                  body="Под фильтр не попала ни одна ссылка. Смените статус или поисковый запрос."
                  action={<Button variant="secondary" onClick={() => { setStatus(''); setQuery(''); }}>Сбросить фильтры</Button>} />
                : <EmptyState icon="link" title="Ссылок пока нет"
                  body="Выставьте первую ссылку в форме выше — она появится здесь со статусом «Активна»." />
            ) : (
              <LinksTable links={links} onRevoke={revoke} revokingId={revoking} />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export { PaymentLinksScreen };
